import {
  DeleteOutlineOutlined,
  DragIndicator,
  ExpandMore,
  SaveOutlined,
  ThumbDownOutlined,
  ThumbUpOutlined,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  TextField,
} from '@mui/material';
import React, { DragEvent, useEffect, useState } from 'react';
import { useTranscriptNoteStyles } from './styles';
import { INote } from '../../global/interfaces';
import SaveIcon from '../../icons/SaveIcon';
interface TranscriptNoteProps {
  id: number;
  note: INote;
  editingNoteRef: React.MutableRefObject<{
    [key: string]: string | null;
  }>;
  collapsed?: boolean;
  saveNote?: (title: string, description: string) => void;
  deleteNote?: (e: any) => void;
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
  disabled?: boolean;
  color?: string;
}

const TranscriptNote = React.forwardRef<HTMLDivElement, TranscriptNoteProps>(
  ({
    id,
    note,
    editingNoteRef,
    collapsed = false,
    saveNote,
    deleteNote,
    onDragStart,
    color,
  }) => {
    const styles = useTranscriptNoteStyles();

    const [title, setTitle] = useState(note.title);
    const [description, setDescription] = useState(note.description);
    const [isExpanded, setIsExpanded] = useState(!collapsed);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
      setHasChanges(title !== note.title || description !== note.description);
    }, [title, description]);

    useEffect(() => {
      setIsExpanded(!collapsed);
    }, [collapsed]);

    useEffect(() => {
      if (hasChanges) editingNoteRef.current[id] = '1';
      else delete editingNoteRef.current[id];
    }, [hasChanges]);

    return (
      <Accordion
        key={id}
        draggable={false && !!saveNote}
        onDragStart={onDragStart}
        expanded={isExpanded}
        onChange={(e, expanded) => {
          (e.target as HTMLElement).closest(
            '.MuiAccordionSummary-expandIconWrapper',
          ) && setIsExpanded(expanded);
        }}
        sx={{
          ...styles.root,
          backgroundColor: color ? color : '#f7f6d4',
          ...(saveNote && { cursor: 'grab' }),
        }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={styles.summaryRoot}>
          <Box sx={styles.summaryContent}>
            {!!saveNote && <DragIndicator sx={styles.dragIcon} />}
            <TextField
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              variant="outlined"
              fullWidth
              placeholder="New Note"
              sx={styles.titleInput}
              InputProps={{
                readOnly: !saveNote,
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails id="accordian_details" sx={styles.detailsRoot}>
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            fullWidth
            variant="outlined"
            placeholder="Add your notes here..."
            sx={styles.descriptionInput}
            InputProps={{
              readOnly: !saveNote,
            }}
          />

          {(note.src === 'a' || note.src === 'u') && (
            <Box id="box_delete" sx={styles.deleteContainer}>
              {note.src === 'a' && (
                <>
                  <IconButton
                    onClick={deleteNote}
                    size="small"
                    sx={styles.button}>
                    <ThumbDownOutlined sx={styles.icon} />
                  </IconButton>
                  <IconButton
                    onClick={deleteNote}
                    size="small"
                    sx={styles.button}>
                    <ThumbUpOutlined sx={styles.icon} />
                  </IconButton>
                </>
              )}
              {note.src === 'u' && (
                <>
                  <IconButton
                    onClick={deleteNote}
                    size="small"
                    sx={styles.button}>
                    <DeleteOutlineOutlined sx={styles.icon} />
                  </IconButton>
                  <IconButton
                    onClick={(e) => {
                      note.title = title;
                      note.description = description;
                      saveNote && saveNote(title, description);
                      setHasChanges(false);
                    }}
                    size="small"
                    sx={styles.button}
                    disabled={!hasChanges}>
                    <SaveIcon strokeWidth={2} sx={styles.icon} />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  },
);

export default TranscriptNote;
